import "./FafWizard.scss";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import Button from "core/buttons/button/Button";
import Icon from "core/icon/Icon";
import ValidationModal from "components/modal/ValidationModal";
import Loader from "core/loader/Loader";
import {
  FILTERS_FOR_NAMES,
  LANG,
  POPUP_STATUS,
  QUERY_STATUS,
} from "constants/constants";
import InfoContext from "pages/InfoContext";
import { canUserPerformAction } from "access-roles/general-functions";
import { FAF_DECLARATION_ROLES } from "access-roles/roles";
import { formatClientOptions } from "components/form/CommonInputs";
import { useDeclarationContext } from "api/datatrack/declaration/DeclarationProvider";
import { useProspectContext } from "api/datatrack/prospect/ProspectProvider";
import { useNeedContext } from "api/mission/need/NeedProvider";
import { useQualifContext } from "api/mission/qualif/QualifProvider";
import { useJobCategoryContext } from "api/settings/job-category/JobCategoryProvider";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { useUserContext } from "api/user/UserProvider";
import WeekStep from "./steps/WeekStep";
import WeekStrip from "./steps/WeekStrip";
import AbsencesStep from "./steps/AbsencesStep";
import InterviewsStep from "./steps/InterviewsStep";
import SourcingStep from "./steps/SourcingStep";
import NeedsStep, { MAX_NEW_NEEDS } from "./steps/NeedsStep";
import QualificationsStep from "./steps/QualificationsStep";
import {
  findOpenWeek,
  formatWeekRange,
  hasFieldError,
  isNeedValid,
  isProspectValid,
  isQualifReviewPending,
  isQualifReviewValid,
  NEED_HAS_DEPENDENCIES_ERROR,
  NEED_WEEKLY_LIMIT_ERROR,
  newQualifDateInputId,
  QUALIF_HAS_DEPENDENCIES_ERROR,
  qualifDateInputId,
  qualifFieldErrors,
  qualifReviewFieldErrors,
  toNeedForm,
  toQualifReviewPayload,
  toQualifForm,
  WEEK_STATES,
} from "utils/fafHelper";

// Steps of the weekly sourcing/recruitment declaration wizard (7 steps).
const STEPS = [
  { key: "week", labelId: "tools_manager_faf_step_week", label: "Semaine" },
  {
    key: "absences",
    labelId: "tools_manager_faf_step_absences",
    label: "Absences",
  },
  {
    key: "interviews",
    labelId: "tools_manager_faf_step_interviews",
    label: "Entretiens",
  },
  {
    key: "sourcing",
    labelId: "tools_manager_faf_step_sourcing",
    label: "Prospections",
  },
  { key: "needs", labelId: "tools_manager_faf_step_needs", label: "Besoins" },
  {
    key: "qualifications",
    labelId: "tools_manager_faf_step_qualifications",
    label: "Qualifications",
  },
  { key: "recap", labelId: "tools_manager_faf_step_recap", label: "Récap" },
];

const INITIAL_FORM = { half_day_absences: 0, recruitment_interview_count: 0 };

// State carried by the card header. Reuses the state labels of the former cells:
// same words, same colours, no new translation key.
const weekCardStatus = (isCompleted, hasDraft) => {
  if (isCompleted)
    return {
      tone: "submitted",
      id: "tools_manager_faf_week_status_submitted",
      label: "Déclarée",
    };
  if (hasDraft)
    return {
      tone: "draft",
      id: "tools_manager_faf_week_status_draft",
      label: "En cours",
    };
  return {
    tone: "open",
    id: "tools_manager_faf_week_status_active",
    label: "À déclarer",
  };
};

// Label of the primary button: resume the draft on the week step, close the
// declaration on the last input step, move on everywhere else.
const primaryLabel = (step, stepKey, hasDraft) => {
  if (step === 1 && hasDraft)
    return { id: "tools_manager_faf_week_resume", defaultMessage: "Reprendre" };
  if (stepKey === "qualifications")
    return { id: "tools_manager_faf_finish", defaultMessage: "Terminer" };
  return { id: "tools_manager_faf_continue", defaultMessage: "Continuer" };
};

// `bad-request` is an array of objects { code, key, params } (cf. apiError.js).
const badRequestCodes = (e) => {
  const badRequest = e?.response?.data?.["bad-request"];
  return Array.isArray(badRequest) ? badRequest.map((err) => err?.code) : [];
};

// The codes worth their own message; anything else falls back to the generic one.
const COMMIT_ERROR_MESSAGES = {
  [NEED_WEEKLY_LIMIT_ERROR]: "tools_manager_faf_need_budget_reached",
  [NEED_HAS_DEPENDENCIES_ERROR]: "tools_manager_faf_need_has_dependencies",
  [QUALIF_HAS_DEPENDENCIES_ERROR]: "tools_manager_faf_qualif_has_dependencies",
};

const commitErrorMessage = (e) => {
  const codes = badRequestCodes(e);
  // Declaration order decides, not the order the response lists the codes in.
  const code = Object.keys(COMMIT_ERROR_MESSAGES).find((c) => codes.includes(c));
  return COMMIT_ERROR_MESSAGES[code] ?? "tools_manager_faf_commit_error";
};

// Visual state of a slot: the step being filled in wins over an already filled one.
const slotState = (active, filled) => {
  if (active) return "active";
  if (filled) return "filled";
  return "idle";
};

const FafWizard = () => {
  const {
    declarationWindow,
    loadWindowStatus,
    getDeclarationWindow,
    getDeclarationById,
    draftDeclaration,
    setDraftDeclaration,
    createDeclaration,
    createStatus,
    updateDeclaration,
    updateStatus,
    submitDeclaration,
    submitStatus,
    submittedDeclaration,
    setSubmittedDeclaration,
  } = useDeclarationContext();
  const { getProspectsByDeclaration, syncProspects } = useProspectContext();
  const {
    needReviews,
    getNeedReviews,
    getNeedReviewsByDeclaration,
    getNeedsByDeclaration,
    saveNeedReview,
    syncNeeds,
  } = useNeedContext();
  const {
    qualifReviews,
    getQualifReviews,
    getQualifReviewsByDeclaration,
    getQualifsByDeclaration,
    saveQualifReview,
    syncQualifs,
  } = useQualifContext();
  const { getJobCategoryId, jobCategoryList, jobCategoryListGetter } =
    useJobCategoryContext();
  const { getNamesByFilters } = useGeneralContext();
  const { lang, userRoleList } = useUserContext();
  const { setCode, setStatus } = useContext(InfoContext);

  const [step, setStep] = useState(1);
  const [committing, setCommitting] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);

  // Step 4 — sourcing: editable list of prospects + server copy for the diff.
  const [prospects, setProspects] = useState([]);
  const [loadedProspects, setLoadedProspects] = useState([]);
  const [sourcingErrors, setSourcingErrors] = useState([]);
  const sourcingLoadedRef = useRef(null);

  // Step 5 — needs: review decisions + new needs.
  const [reviewMap, setReviewMap] = useState({});
  const [newNeeds, setNewNeeds] = useState([]);
  // Snapshot of needs already synced with the server (with their id): serves as a
  // diff reference so a need is not recreated on each visit to the step.
  const [syncedNeeds, setSyncedNeeds] = useState([]);
  const [showNewNeed, setShowNewNeed] = useState(false);
  const [reviewError, setReviewError] = useState(false);
  const [needErrors, setNeedErrors] = useState([]);

  // Step 6 — qualifications: status per qualif + new qualif.
  const [statusMap, setStatusMap] = useState({});
  // Last answer the server holds per qualif: the rollback target of a failed save.
  const savedReviewsRef = useRef({});
  const [showNewQualif, setShowNewQualif] = useState(false);
  const [newQualifs, setNewQualifs] = useState([]);
  // Snapshot of qualifs already synced with the server (with their id): diff reference
  // so a qualif is not recreated on each re-commit (retry after a submit failure).
  const [syncedQualifs, setSyncedQualifs] = useState([]);
  const [qualifReviewError, setQualifReviewError] = useState(false);
  const [newQualifErrors, setNewQualifErrors] = useState([]);

  // The back strictly caps at 3 new needs per declaration
  // (need_weekly_limit_reached error beyond that). Step commit error message.
  const [commitError, setCommitError] = useState(null);

  useEffect(() => {
    getDeclarationWindow();
  }, [getDeclarationWindow]);

  // Loads the prospects when entering the sourcing step; re-keyed on the draft id
  // (reloads if a new draft appears, does not overwrite edits in progress).
  const stepKey = STEPS[step - 1]?.key;
  useEffect(() => {
    const draftId = draftDeclaration?.id;
    if (
      stepKey === "sourcing" &&
      draftId &&
      sourcingLoadedRef.current !== draftId
    ) {
      sourcingLoadedRef.current = draftId;
      getProspectsByDeclaration(draftId).then((list) => {
        setProspects(list || []);
        setLoadedProspects(list || []);
      });
    }
  }, [stepKey, draftDeclaration?.id, getProspectsByDeclaration]);

  // Weekly review: loads the list of needs/qualifs to review for the
  // current declaration when entering the relevant step (the provider re-keys per declaration).
  // jobCategoryListGetter: required to rebuild the category of hydrated needs, which
  // now follows from their job (redmine 12940).
  useEffect(() => {
    const draftId = draftDeclaration?.id;
    if (stepKey === "needs" && draftId) {
      getNeedReviews(draftId);
      jobCategoryListGetter();
    }
  }, [stepKey, draftDeclaration?.id, getNeedReviews, jobCategoryListGetter]);

  // The review (need-review) already excludes the needs of the current declaration;
  // those are fetched by a dedicated call (GET /need?declaration_ids=<décl>) and are
  // displayed as « en cours d'ajout ». Hydrated once per declaration, without
  // overwriting an input in progress; waits for the reference data (root category).
  // A need only carries client_id: one grouped call resolves the labels of the
  // reference clients. Free text already carries its own name.
  const resolveClientLabels = useCallback(
    async (clientIds) => {
      if (!clientIds.length) return new Map();
      try {
        const clients = await getNamesByFilters({
          types: [FILTERS_FOR_NAMES.CLIENT],
          id: clientIds,
        });
        return new Map(
          formatClientOptions(clients).map((c) => [c.value, c.label])
        );
      } catch (e) {
        console.error("faf resolve client labels", e); // fallback: no label, non-blocking
        return new Map();
      }
    },
    [getNamesByFilters]
  );

  // Review cards name the client of each need, reference clients included.
  const [reviewClientLabels, setReviewClientLabels] = useState(new Map());
  useEffect(() => {
    const clientIds = needReviews
      .map(({ need }) => need?.client_id)
      .filter(Boolean);
    let cancelled = false;
    resolveClientLabels(clientIds).then((labels) => {
      if (!cancelled) setReviewClientLabels(labels);
    });
    return () => {
      cancelled = true;
    };
  }, [needReviews, resolveClientLabels]);

  const needsHydratedRef = useRef(null);
  useEffect(() => {
    const draftId = draftDeclaration?.id;
    if (stepKey !== "needs" || !draftId) return;
    if (needsHydratedRef.current === draftId) return;
    if (!jobCategoryList.length) return; // reference data not loaded yet → we will retry
    needsHydratedRef.current = draftId; // set immediately: avoids a concurrent double fetch
    (async () => {
      try {
        const list = await getNeedsByDeclaration(draftId);
        if (!list.length) return;
        const forms = list.map((need) => toNeedForm(need, getJobCategoryId));
        // Without this the client tag shows empty on resume.
        const labelById = await resolveClientLabels(
          forms.map((f) => f.client_id).filter(Boolean)
        );
        forms.forEach((f) => {
          if (f.client_id)
            f.client_label = labelById.get(f.client_id) ?? f.client_label;
        });
        setNewNeeds((prev) => (prev.length ? prev : forms));
        setSyncedNeeds((prev) => (prev.length ? prev : forms));
        setShowNewNeed(true);
      } catch (e) {
        // Edit list failure: we release the guard to retry (otherwise the needs
        // already created would stay invisible/non-editable for this declaration).
        needsHydratedRef.current = null;
        console.error("faf hydrate needs", e);
      }
    })();
  }, [
    stepKey,
    draftDeclaration?.id,
    jobCategoryList,
    getJobCategoryId,
    getNeedsByDeclaration,
    resolveClientLabels,
  ]);

  useEffect(() => {
    const draftId = draftDeclaration?.id;
    if (stepKey === "qualifications" && draftId) getQualifReviews(draftId);
  }, [stepKey, draftDeclaration?.id, getQualifReviews]);

  // The review (qualif-review) already excludes the qualifs of the current declaration;
  // those are fetched by a dedicated call (GET /qualif?declaration_ids=<décl>) and are
  // displayed as « en cours d'ajout ». Hydrated once per declaration.
  const qualifsHydratedRef = useRef(null);
  useEffect(() => {
    const draftId = draftDeclaration?.id;
    if (stepKey !== "qualifications" || !draftId) return;
    if (qualifsHydratedRef.current === draftId) return;
    qualifsHydratedRef.current = draftId; // set immediately: avoids a concurrent double fetch
    (async () => {
      try {
        const list = await getQualifsByDeclaration(draftId);
        if (!list.length) return;
        const forms = list.map((qualif) => toQualifForm(qualif));
        setNewQualifs((prev) => (prev.length ? prev : forms));
        setSyncedQualifs((prev) => (prev.length ? prev : forms));
        setShowNewQualif(true);
      } catch (e) {
        // Edit list failure: we release the guard to retry.
        qualifsHydratedRef.current = null;
        console.error("faf hydrate qualifs", e);
      }
    })();
  }, [stepKey, draftDeclaration?.id, getQualifsByDeclaration]);

  // Pre-fills answers already given (resumed declaration) without overwriting an input
  // in progress: we only fill entries not yet present in the local map.
  useEffect(() => {
    setReviewMap((prev) => {
      const next = { ...prev };
      needReviews.forEach(({ need, still_active }) => {
        if (next[need.id] === undefined && still_active != null)
          next[need.id] = still_active;
      });
      return next;
    });
  }, [needReviews]);

  useEffect(() => {
    const seed = {};
    qualifReviews.forEach(({ qualif, status, ...review }) => {
      if (status)
        seed[qualif.id] = {
          status,
          qualification_date: review.qualification_date || "",
          new_candidate: review.new_candidate || null,
        };
    });
    Object.assign(savedReviewsRef.current, seed);
    setStatusMap((prev) => {
      const next = { ...prev };
      Object.entries(seed).forEach(([id, review]) => {
        if (next[id] === undefined) next[id] = review;
      });
      return next;
    });
  }, [qualifReviews]);

  // /window carousel: the OPEN cell is the only week declarable right now.
  const weeks = declarationWindow?.weeks;
  const upToDate = declarationWindow?.up_to_date;
  const activeWeekStart = findOpenWeek(weeks)?.week_start_date;
  // One draft at a time: as long as a declaration is in progress (DRAFT), we
  // forbid starting a new one (the OPEN week) — the draft must be finished first.
  // The primary button of step 1 then resumes that draft (the oldest one).
  const draftWeek = (weeks || []).find((w) => w.state === WEEK_STATES.DRAFT);
  const hasDraft = !!draftWeek;
  const draftDeclarationId = draftWeek?.declaration_id ?? null;
  // Submitted declaration (recap reached): frozen server-side (SUBMITTED). Any rewrite
  // would be rejected (faf_declaration_not_draft) → we switch the wizard to read-only.
  // The final submission is the one CRUD write of the wizard the user commits
  // to explicitly, so it reports through the EOS ValidationModal like every
  // other one. The flag is local: `submitStatus` survives a soft restart, and
  // the modal must only answer for the click that has just happened.
  // Card header: the declared week and its state, present at every step.
  // Everything is derived from the window already loaded — no extra call.
  const cardWeekStart = draftWeek?.week_start_date || activeWeekStart;
  const cardWeekRange = formatWeekRange({
    isoDate: cardWeekStart,
    locale: lang === LANG.FR ? "fr-FR" : "en-US",
  });

  const isCompleted = !!submittedDeclaration;
  const cardStatus = weekCardStatus(isCompleted, hasDraft);

  // The five slots of the card. `step` stays the wizard step: a slot carries
  // its step, not the other way round — the `commitCurrentStep` dispatch is intact.
  const SLOTS = [
    {
      key: "absences",
      step: 2,
      icon: "vacation",
      labelId: "tools_manager_faf_step_absences",
      label: "Absences",
      unitId: "tools_manager_faf_slot_unit_absences",
      unit: "demi-journées",
      value: () => form.half_day_absences ?? 0,
    },
    {
      key: "interviews",
      step: 3,
      icon: "event",
      labelId: "tools_manager_faf_step_interviews",
      label: "Entretiens",
      unitId: "tools_manager_faf_slot_unit_interviews",
      unit: "entretiens",
      value: () => form.recruitment_interview_count ?? 0,
    },
    {
      key: "sourcing",
      step: 4,
      icon: "search",
      labelId: "tools_manager_faf_step_sourcing",
      label: "Prospections",
      unitId: "tools_manager_faf_slot_unit_sourcing",
      unit: "prospections",
      value: () => prospects.length,
      chips: () =>
        prospects.map((p, i) =>
          `${p.first_name || ""} ${p.last_name || ""}`.trim() ||
          `Prospection ${i + 1}`
        ),
    },
    {
      key: "needs",
      step: 5,
      icon: "folder",
      labelId: "tools_manager_faf_step_needs",
      label: "Besoins",
      unitId: "tools_manager_faf_slot_unit_needs",
      unit: "nouveaux besoins",
      value: () => newNeeds.length,
      chips: () =>
        newNeeds.map(
          (n, i) => n.client_label || n.client_name_freetext || `Besoin ${i + 1}`
        ),
    },
    {
      key: "qualifications",
      step: 6,
      icon: "check-list",
      labelId: "tools_manager_faf_step_qualifications",
      label: "Qualifications",
      unitId: "tools_manager_faf_slot_unit_qualifs",
      unit: "nouvelles qualifs",
      value: () => newQualifs.length,
      chips: () =>
        newQualifs.map((q, i) =>
          `${q.candidate_first_name || ""} ${q.candidate_last_name || ""}`.trim() ||
          `Qualification ${i + 1}`
        ),
    },
  ];

  const isSaving =
    committing ||
    createStatus === QUERY_STATUS.LOADING ||
    updateStatus === QUERY_STATUS.LOADING;

  const setField = (name, value) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  // Need review: answer saved immediately (PATCH) on click, editable by re-clicking.
  // On PATCH failure we roll back the optimistic value and surface the error: otherwise the
  // UI would show the answer as saved while /submit scores the week without it (silent loss).
  const setReview = (needId, value) => {
    const previous = reviewMap[needId];
    setReviewMap((prev) => ({ ...prev, [needId]: value }));
    saveNeedReview(draftDeclaration?.id, {
      need_id: needId,
      still_active: value,
    }).catch((e) => {
      console.error("saveNeedReview", e);
      setReviewMap((prev) => ({ ...prev, [needId]: previous }));
      setCommitError("tools_manager_faf_commit_error");
    });
  };

  // Qualif review: answer saved as soon as it is complete (rank date or new candidate).
  // Same rollback + error surfacing as setReview (a swallowed PATCH would lose the status).
  const setQualifStatus = (qualifId, patch) => {
    const nextReview = { ...statusMap[qualifId], ...patch };
    setStatusMap((prev) => ({ ...prev, [qualifId]: nextReview }));
    if (isQualifReviewValid(nextReview)) {
      saveQualifReview(
        draftDeclaration?.id,
        toQualifReviewPayload(qualifId, nextReview)
      )
        .then(() => {
          savedReviewsRef.current[qualifId] = nextReview;
        })
        .catch((e) => {
          console.error("saveQualifReview", e);
          // Skip if answered again since; otherwise back to what the server holds.
          setStatusMap((prev) => {
            if (prev[qualifId] !== nextReview) return prev;
            const next = { ...prev };
            const saved = savedReviewsRef.current[qualifId];
            if (saved) next[qualifId] = saved;
            else delete next[qualifId];
            return next;
          });
          setCommitError(commitErrorMessage(e));
        });
    }
  };

  // Step 1: button enabled if there is a draft to resume OR a week to declare.
  const canContinue =
    step === 1 ? hasDraft || (!upToDate && !!activeWeekStart) : true;

  // Shared budget of new needs: those from the needs step (5) + those
  // created on the fly by the new qualifs (6), capped together at MAX_NEW_NEEDS.
  const newNeedsUsed =
    (showNewNeed ? newNeeds.length : 0) +
    (showNewQualif ? newQualifs.filter((q) => q.use_new_need).length : 0);
  const needBudgetReached = newNeedsUsed >= MAX_NEW_NEEDS;

  const persistDraft = async () => {
    if (draftDeclaration?.id) {
      await updateDeclaration(draftDeclaration.id, form);
    } else {
      await createDeclaration(form);
    }
  };

  // Step 4 → syncs the prospects (create/update/delete) vs the server copy.
  const commitSourcing = async () => {
    const errs = prospects.map((p) => !isProspectValid(p));
    if (errs.some(Boolean)) {
      setSourcingErrors(errs);
      return false;
    }
    setSourcingErrors([]);
    // Sync create/update/delete vs the previous snapshot (no duplicate on return).
    // We reinject the reconciled list (server ids) as new state + snapshot.
    const synced = await syncProspects({
      declarationId: draftDeclaration?.id,
      prospects,
      loaded: loadedProspects,
    });
    setProspects(synced);
    setLoadedProspects(synced);
    return true;
  };

  // Step 5 → reviews are already saved on click (PATCH); we validate that they are all
  // filled in, then we create the new needs.
  const commitNeeds = async () => {
    const unreviewed = needReviews.some(
      ({ need }) => reviewMap[need.id] === undefined
    );
    // The toggle only masks the UI: hiding it must not drop already-created needs (syncNeeds
    // would DELETE them, incl. those hydrated from a prior session) — persisted needs (id) are
    // always kept; their removal stays explicit, via each NeedForm's "Supprimer" button. But an
    // id-less draft the user opened then abandoned by toggling OFF must not block "Continue" with
    // errors on a hidden form: discard those unsaved drafts when the section is closed.
    const activeNewNeeds = showNewNeed ? newNeeds : newNeeds.filter((n) => n.id);
    const nErrs = activeNewNeeds.map((n) => !isNeedValid(n));
    if (unreviewed || nErrs.some(Boolean)) {
      setReviewError(unreviewed);
      setNeedErrors(nErrs);
      return false;
    }
    setReviewError(false);
    setNeedErrors([]);
    // Sync create/update/delete vs the previous snapshot (no duplicate on return).
    // We reinject the reconciled list (server ids) as new state + snapshot.
    const synced = await syncNeeds({
      declarationId: draftDeclaration?.id,
      needs: activeNewNeeds,
      loaded: syncedNeeds,
    });
    setNewNeeds(synced);
    setSyncedNeeds(synced);
    return true;
  };

  // Banner for a missing qualification date or candidate; returns the DOM id of the
  // date to focus, from the review banners first then the qualifs being added.
  const alertMissingQualifInput = (pending, newQualifFieldErrs) => {
    const errorsOf = ({ qualif }) => qualifReviewFieldErrors(statusMap[qualif.id]);
    const missingReview = pending.find((r) => errorsOf(r).qualification_date);
    // The index keys both the rendered form and its date input: a missing date
    // implies no server id, so the list is the unfiltered one being rendered.
    const missingNewIndex = newQualifFieldErrs.findIndex(
      (e) => e.qualification_date
    );
    const missingCandidate =
      newQualifFieldErrs.some((e) => e.candidate) ||
      pending.some((r) => errorsOf(r).new_candidate);
    // Review banners first; then the candidate, the first field of a card being
    // added, before that same card's date. Message and focus always agree.
    let code = null;
    let dateInputId = null;
    if (missingReview) {
      code = "tools_manager_faf_qualif_date_required";
      dateInputId = qualifDateInputId(missingReview.qualif.id);
    } else if (missingCandidate) {
      code = "tools_manager_faf_candidate_required";
    } else if (missingNewIndex >= 0) {
      code = "tools_manager_faf_qualif_date_required";
      dateInputId = newQualifDateInputId(missingNewIndex);
    }
    if (code) {
      setStatus(POPUP_STATUS.ERROR);
      setCode(code);
    }
    return dateInputId;
  };

  // Step 6 → reviews already saved on click (PATCH); we validate that they are complete,
  // then we create the new qualifs.
  const commitQualifs = async () => {
    const pending = qualifReviews.filter((r) =>
      isQualifReviewPending(r, statusMap)
    );
    const unreviewed = pending.length > 0;
    // Same as commitNeeds: the toggle only masks the UI — hiding it must not DELETE
    // already-created qualifs (id kept, removal explicit via each QualifForm's "Supprimer"),
    // but an id-less draft abandoned by toggling OFF must not block "Continue" on a hidden form.
    const activeNewQualifs = showNewQualif
      ? newQualifs
      : newQualifs.filter((q) => q.id);
    const qFieldErrs = activeNewQualifs.map(qualifFieldErrors);
    const qErrs = qFieldErrs.map(hasFieldError);
    if (unreviewed || qErrs.some(Boolean)) {
      setQualifReviewError(unreviewed);
      setNewQualifErrors(qErrs);
      const missingDateInputId = alertMissingQualifInput(pending, qFieldErrs);
      // No scroll: the alert banner scrolls to the top and must stay in view.
      if (missingDateInputId) {
        document
          .getElementById(missingDateInputId)
          ?.focus({ preventScroll: true });
      }
      return false;
    }
    setQualifReviewError(false);
    setNewQualifErrors([]);
    // Sync create/delete vs the previous snapshot (no duplicate on re-commit).
    const synced = await syncQualifs({
      declarationId: draftDeclaration?.id,
      qualifs: activeNewQualifs,
      loaded: syncedQualifs,
    });
    setNewQualifs(synced);
    setSyncedQualifs(synced);
    return true;
  };

  // Persists according to the current step before advancing; a failure blocks navigation.
  // Dispatch by step key (STEPS = single source), not by hardcoded index.
  const commitCurrentStep = async () => {
    // Declaration already submitted: navigation stays possible in read mode, but we
    // never re-PATCH/re-submit a frozen record (the back rejects any write
    // on a non-DRAFT declaration → faf_declaration_not_draft).
    if (isCompleted) return true;
    switch (stepKey) {
      case "interviews":
        await persistDraft();
        return true;
      case "sourcing":
        return commitSourcing();
      case "needs":
        return commitNeeds();
      case "qualifications": {
        const ok = await commitQualifs();
        if (!ok) return false;
        // Final submission: the back computes the score and freezes the declaration.
        // A failure (server validation) rejects → handleNext does not block navigation.
        setSubmitModalOpen(true);
        await submitDeclaration(draftDeclaration?.id);
        return true;
      }
      default:
        return true;
    }
  };

  const handleNext = async () => {
    setCommitting(true);
    setCommitError(null);
    try {
      const ok = await commitCurrentStep();
      if (ok) setStep((s) => Math.min(STEPS.length, s + 1));
    } catch (e) {
      console.error("faf commit step", step, e);
      setCommitError(commitErrorMessage(e));
    } finally {
      setCommitting(false);
    }
  };

  const handleBack = () => setStep((s) => Math.max(1, s - 1));

  // Resuming a draft (click on a DRAFT cell): reloads the detail, pre-fills
  // the form (absences/interviews) and enters step 2 (step 1 « Semaine » is purely
  // for display). The child entities reload at the relevant steps; the reviews are
  // idempotent (upsert), so re-visiting already-done steps does not create a duplicate.
  // Purges the per-step working state (sourcing/needs/qualifs + loading
  // guards): shared by resuming a draft and the post-recap restart,
  // so an input inherited from a previous flow is never attached to the current draft.
  const resetStepState = () => {
    setProspects([]);
    setLoadedProspects([]);
    setSourcingErrors([]);
    setReviewMap({});
    setNewNeeds([]);
    setSyncedNeeds([]);
    setShowNewNeed(false);
    setReviewError(false);
    setNeedErrors([]);
    needsHydratedRef.current = null;
    setStatusMap({});
    savedReviewsRef.current = {};
    setShowNewQualif(false);
    setNewQualifs([]);
    setSyncedQualifs([]);
    setQualifReviewError(false);
    setNewQualifErrors([]);
    qualifsHydratedRef.current = null;
    sourcingLoadedRef.current = null;
  };

  // Resume: no progress is stored server-side (the declaration does not carry
  // a current step). We therefore infer the furthest step already worked on — qualifs (6)
  // > needs (5) > sourcing (4) — otherwise step 2 (editable start). A step is
  // « worked on » if new entities were created there OR if the associated reviews were
  // reviewed: one may create no new need but have answered the reviews of the needs
  // in progress, which counts just as much. A failed fetch = « no data ».
  const resumeStepFor = async (declarationId) => {
    const [prospects, needs, qualifs, needRevs, qualifRevs] = await Promise.all([
      getProspectsByDeclaration(declarationId).catch(() => []),
      getNeedsByDeclaration(declarationId).catch(() => []),
      getQualifsByDeclaration(declarationId).catch(() => []),
      getNeedReviewsByDeclaration(declarationId).catch(() => []),
      getQualifReviewsByDeclaration(declarationId).catch(() => []),
    ]);
    // The 5 lists are guaranteed to be arrays (each promise .catch → []).
    // Reviewed review: status filled (qualif) / still_active filled (need).
    if (qualifs.length || qualifRevs.some((r) => r.status != null)) return 6;
    if (needs.length || needRevs.some((r) => r.still_active != null)) return 5;
    if (prospects.length) return 4;
    return 2;
  };

  const handleResume = async (declarationId) => {
    if (!declarationId) return;
    setCommitting(true);
    try {
      const decl = await getDeclarationById(declarationId);
      if (decl) {
        resetStepState();
        setForm({
          half_day_absences: decl.half_day_absences ?? 0,
          recruitment_interview_count: decl.recruitment_interview_count ?? 0,
        });
        setSubmittedDeclaration(null);
        setStep(await resumeStepFor(declarationId));
      }
    } catch (e) {
      console.error("faf resume draft", declarationId, e);
    } finally {
      setCommitting(false);
    }
  };

  // Primary button of step 1: if a draft is in progress, it resumes
  // that draft (like a click on its cell); otherwise it starts the OPEN week.
  const handlePrimary = () => {
    if (step === 1 && hasDraft) {
      handleResume(draftDeclarationId);
    } else {
      handleNext();
    }
  };

  // New declaration from the recap: resets the wizard and resyncs
  // with the server state (GET /window) rather than a simple local reset.
  const softRestart = () => {
    resetStepState();
    setForm(INITIAL_FORM);
    // Clears the current draft: without this, the next declaration would reuse the frozen
    // id of the previous one and PATCH a SUBMITTED declaration (faf_declaration_not_draft).
    setDraftDeclaration(null);
    setSubmittedDeclaration(null);
    setStep(1);
    getDeclarationWindow();
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <WeekStep
            upToDate={upToDate}
            openWeekStart={activeWeekStart}
            blockedByDraft={hasDraft}
          />
        );
      case 2:
        return (
          <AbsencesStep
            value={form.half_day_absences}
            onChange={(v) => setField("half_day_absences", v)}
          />
        );
      case 3:
        return (
          <InterviewsStep
            value={form.recruitment_interview_count}
            onChange={(v) => setField("recruitment_interview_count", v)}
          />
        );
      case 4:
        return (
          <SourcingStep
            prospects={prospects}
            setProspects={setProspects}
            lang={lang}
            showErrors={sourcingErrors}
          />
        );
      case 5:
        return (
          <NeedsStep
            reviewNeeds={needReviews}
            clientLabelById={reviewClientLabels}
            reviewMap={reviewMap}
            setReview={setReview}
            newNeeds={newNeeds}
            setNewNeeds={setNewNeeds}
            showNewNeed={showNewNeed}
            setShowNewNeed={setShowNewNeed}
            reviewError={reviewError}
            needErrors={needErrors}
            needBudgetReached={needBudgetReached}
          />
        );
      case 6:
        return (
          <QualificationsStep
            reviewQualifs={qualifReviews}
            statusMap={statusMap}
            setQualifStatus={setQualifStatus}
            showNewQualif={showNewQualif}
            setShowNewQualif={setShowNewQualif}
            newQualifs={newQualifs}
            setNewQualifs={setNewQualifs}
            qualifReviewError={qualifReviewError}
            newQualifErrors={newQualifErrors}
            needBudgetReached={needBudgetReached}
            lang={lang}
          />
        );
      // No case 7: step 7 is only ever reached once the submit has resolved, and
      // `isCompleted` then swaps the whole workshop for the sealed band, so the
      // former RecapStep could never render. The band carries the closing state.
      default:
        return (
          <div className="faf-step faf-step--placeholder">
            <span className="faf-eyebrow">
              <FormattedMessage
                id={STEPS[step - 1].labelId}
                defaultMessage={STEPS[step - 1].label}
              />
            </span>
            <p className="faf-placeholder-text">
              <FormattedMessage
                id="tools_manager_faf_step_coming_soon"
                defaultMessage="Cette étape arrive dans une prochaine livraison."
              />
            </p>
          </div>
        );
    }
  };

  // Spinner only until a first window lands: later refetches refresh the card in place.
  const canLoadWindow = canUserPerformAction({
    userRoles: userRoleList,
    requiredRoles: FAF_DECLARATION_ROLES.ROLE_GET_DECLARATION,
  });
  if (
    canLoadWindow &&
    !declarationWindow &&
    [QUERY_STATUS.INITIAL, QUERY_STATUS.LOADING].includes(loadWindowStatus)
  ) {
    return <Loader id="faf-loader" />;
  }

  if (loadWindowStatus === QUERY_STATUS.ERROR) {
    return (
      <div className="faf-wizard faf-wizard--error">
        <FormattedMessage
          id="tools_manager_faf_load_error"
          defaultMessage="Impossible de charger ta déclaration. Réessaie plus tard."
        />
      </div>
    );
  }

  const isLastStep = step === STEPS.length;

  return (
    <div className="faf-wizard">
      <WeekStrip
        weeks={weeks}
        currentWeekStart={cardWeekStart}
        openWeekStart={activeWeekStart}
        onResume={handleResume}
        lang={lang}
      />

      <div className={`faf-card ${isCompleted ? "faf-card--sealed" : ""}`}>
        <div className="faf-card-head">
          <div>
            <span className="faf-card-head-label">
              <FormattedMessage
                id="tools_manager_faf_spine_week"
                defaultMessage="Tu déclares"
              />
            </span>
            <h2 className="faf-card-head-week">{cardWeekRange || "—"}</h2>
            <span
              className={`faf-card-status faf-card-status--${cardStatus.tone}`}
            >
              <span className="faf-card-status-dot" aria-hidden="true" />
              <FormattedMessage
                id={cardStatus.id}
                defaultMessage={cardStatus.label}
              />
            </span>
          </div>
          {isCompleted && submittedDeclaration?.points_total != null && (
            <div className="faf-card-score">
              <b>{submittedDeclaration.points_total}</b>
              <span>
                <FormattedMessage
                  id="tools_manager_faf_recap_points"
                  defaultMessage="points cumulés"
                />
              </span>
            </div>
          )}
        </div>

        {/* The five figures of the week, always on screen. One slot per input
            step (2 to 6): the wizard step does not change, only the way it is
            shown. A slot only shows its figure once its step has been walked
            through — before that the matching list is not loaded and a zero
            would be wrong. */}
        <div className="faf-slots">
          {SLOTS.map((slot) => {
            const visited = step > slot.step;
            const active = step === slot.step;
            const value = slot.value();
            const slotChips = slot.chips ? slot.chips() : [];
            const filled = visited && value > 0;
            // Same rule as the former stepper: we only go back to a step already
            // walked through, and never once the week is sealed.
            const clickable = slot.step <= step && !isCompleted;

            return (
              <button
                key={slot.key}
                type="button"
                className="faf-slot"
                data-testid={`faf-stepper-${slot.key}`}
                data-state={slotState(active, filled)}
                data-kind={slot.key}
                disabled={!clickable}
                onClick={() => setStep(slot.step)}
              >
                <span className="faf-slot-head">
                  <Icon
                    icon={slot.icon}
                    color={filled || active ? "violet" : "violet-clair"}
                    width={15}
                    height={15}
                  />
                  <span className="faf-slot-name">
                    <FormattedMessage
                      id={slot.labelId}
                      defaultMessage={slot.label}
                    />
                  </span>
                </span>
                <span className="faf-slot-value">
                  {visited || active ? value : "—"}
                </span>
                <span className="faf-slot-unit">
                  <FormattedMessage
                    id={slot.unitId}
                    defaultMessage={slot.unit}
                  />
                </span>
                {/* What the slot holds, not only how many: two labels then a
                    "+N". Nothing before the step has been walked through — the
                    list is not loaded. */}
                {(visited || active) && slotChips.length > 0 && (
                  <span className="faf-slot-chips">
                    {slotChips.slice(0, 2).map((label) => (
                      <span className="faf-slot-chip" key={label}>
                        {label}
                      </span>
                    ))}
                    {slotChips.length > 2 && (
                      <span className="faf-slot-chip">
                        {`+${slotChips.length - 2}`}
                      </span>
                    )}
                  </span>
                )}
                {!visited && !active && (
                  <span className="faf-slot-state">
                    <FormattedMessage
                      id="tools_manager_faf_slot_locked"
                      defaultMessage="verrouillée"
                    />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Once the week is sealed, the workshop gives way to the closing band:
            the score is already in the header, there is nothing left to fill in. */}
        {isCompleted ? (
          <div className="faf-sealed">
            <span className="faf-sealed-mark" aria-hidden="true">
              <Icon icon="tick-gray" color="green" width={18} height={18} />
            </span>
            <span className="faf-sealed-text">
              <b>
                <FormattedMessage
                  id="tools_manager_faf_sealed_title"
                  defaultMessage="Semaine du {date} déclarée"
                  values={{ date: cardWeekRange }}
                />
              </b>
              <FormattedMessage
                id="tools_manager_faf_sealed_desc"
                defaultMessage="Les points sont calculés par le serveur."
              />
            </span>
            <Button
              size="medium"
              color="deep-purple"
              className="faf-sealed-action"
              data-testid="faf-recap-restart"
              onClick={softRestart}
            >
              <FormattedMessage
                id="tools_manager_faf_recap_restart"
                defaultMessage="Nouvelle déclaration"
              />
            </Button>
          </div>
        ) : (
        <div className="faf-content">
          {/* Progress marker: the current slot among the five. */}
          <span className="faf-bench-step">
            <FormattedMessage
              id="tools_manager_faf_bench_step"
              defaultMessage="Case {n} sur {total}"
              values={{ n: Math.max(1, step - 1), total: SLOTS.length }}
            />
          </span>
          <div className="faf-content-body">{renderStep()}</div>

        {commitError && (
          <p className="faf-commit-error">
            <FormattedMessage
              id={commitError}
              defaultMessage="Une erreur est survenue, réessaie."
            />
          </p>
        )}

        {!isLastStep && (
          <div className="faf-footer">
            {step > 1 ? (
              <Button
                className="btn-outline"
                color="btn-medium gray-700"
                data-testid="faf-nav-back"
                onClick={handleBack}
              >
                <FormattedMessage
                  id="tools_manager_faf_back"
                  defaultMessage="Retour"
                />
              </Button>
            ) : (
              <span />
            )}
            <Button
              size="medium"
              color="deep-purple"
              data-testid="faf-nav-primary"
              onClick={handlePrimary}
              disabled={!canContinue || isSaving}
            >
              <FormattedMessage {...primaryLabel(step, stepKey, hasDraft)} />
            </Button>
          </div>
        )}
        </div>
        )}
      </div>

      <ValidationModal
        open={submitModalOpen}
        status={submitStatus}
        onClose={() => setSubmitModalOpen(false)}
        messages={{
          pendingTitleId: "tools_manager_faf_submit_pending_title",
          pendingTitleDefault: "Enregistrement de ta semaine...",
          pendingMessageId: "tools_manager_faf_submit_pending_msg",
          pendingMessageDefault: "Le calcul de tes points est en cours.",
          successTitleId: "tools_manager_faf_submit_success_title",
          successTitleDefault: "Semaine déclarée",
          successMessageId: "tools_manager_faf_submit_success_msg",
          successMessageDefault: "Ta déclaration est enregistrée.",
          errorTitleId: "tools_manager_faf_submit_error_title",
          errorTitleDefault: "Déclaration non enregistrée",
          errorMessageId: "tools_manager_faf_submit_error_msg",
          errorMessageDefault:
            "Ta semaine n'a pas pu être enregistrée. Réessaie dans un instant.",
        }}
      />
    </div>
  );
};

export default FafWizard;
